/* aw-zip-gate.js
 * ZIP delivery-eligibility gate: served (free) / nearby (surcharge) / beyond (waitlist).
 * Data lazy-loads on first focus so it never blocks LCP. Zero network on input.
 * Persists { state, zip, fee } to localStorage + cart attributes; the order widget
 * and checkout enforcement read the same values.
 */
/* Stored-verdict stamp (Brian, 2026-07-21). Bump CACHE_VERSION whenever the
 * meaning of a stored field changes. The stamp rides alongside the resolve()
 * result, never replaces it: both arms read state, zip, fee, miles, cadence and
 * note off this object at boot, so its shape must not change. Declared above
 * init() because init() reads the cache while this module is still evaluating. */
const CACHE_VERSION = 1;
const CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

// Up here for the same reason: the returning-visitor restore calls loadData()
// synchronously, before this module finishes evaluating.
let DATA = null;

/* Cart-attribute write queue (Brian, 2026-07-21). persist() and
 * clearEligibility() POST the same five keys, so two checks inside one round
 * trip could land out of order: a waitlist ZIP corrected to a served one could
 * finish with localStorage saying served while the cart carried no Delivery
 * attributes at all, which is also how the Oregon pre-order hold flag goes
 * missing. Chaining every write onto the previous one keeps the cart's order
 * the same as the call order. Declared up here because init() can clear an
 * expired verdict while this module is still evaluating. */
let lastWrite = Promise.resolve();

const root = document.getElementById('aw-zip-gate');
if (root) init(root);

function cfg(el) {
  return {
    servedUrl: el.dataset.servedUrl,
    surchargeUrl: el.dataset.surchargeUrl,
    regionsUrl: el.dataset.regionsUrl,
    notesUrl: el.dataset.notesUrl,
    mode: el.dataset.feeMode,
    flatFee: Number(el.dataset.flatFee),
    per5: Number(el.dataset.per5),
    maxMiles: Number(el.dataset.maxMiles),
    feeCap: Number(el.dataset.feeCap) || 0,
    klaviyoKey: el.dataset.klaviyoKey,
    klaviyoList: el.dataset.klaviyoList,
  };
}

/* Brian, 2026-07-21: a 429 burst, a CDN blip or an HTML 404 body used to reject
 * here, which killed the submit handler as an unhandled rejection and left the
 * customer with no error and no state change. Every fetch now fails soft, the
 * caller shows a retryable message, and a failed load is never cached, so the
 * next Check can still succeed. */
async function loadData(c) {
  if (DATA) return DATA;
  const json = (url) => fetch(url)
    .then((r) => {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    })
    .catch(() => null);
  const [served, surcharge, regions, notes] = await Promise.all([
    json(c.servedUrl),
    json(c.surchargeUrl),
    c.regionsUrl ? json(c.regionsUrl) : Promise.resolve(null),
    c.notesUrl ? json(c.notesUrl) : Promise.resolve(null),
  ]);
  // Ordering is hard-gated on eligibility, so a missing served/surcharge list is
  // a hard stop, not a degraded result. Regions and notes stay optional.
  if (!Array.isArray(served) || !surcharge || typeof surcharge !== 'object') return null;
  DATA = { served: new Set(served.map(String)), surcharge, regions, notes };
  return DATA;
}

/* Route-zone cadence for a ZIP ({ label, weeks }), or null for the default zones. */
function regionFor(zip, data) {
  const r = data.regions;
  const key = r && r.zips && r.zips[zip];
  return (key && r.regions[key]) || null;
}

/* Coming-soon / informational note for a served ZIP ({ note, cta }), or null.
 * Oregon (2026-07-19, Brian): the 16 Portland-area ZIPs are orderable now but
 * delivery starts Q4 2026, so a served result also shows the note. */
function noteFor(zip, data) {
  const groups = data.notes;
  if (!Array.isArray(groups)) return null;
  for (const g of groups) {
    if (g && g.zips && g.zips.indexOf(zip) > -1) return { note: g.note || '', cta: g.cta || '' };
  }
  return null;
}

function feeForMiles(miles, c) {
  if (miles == null || miles > c.maxMiles) return null;
  if (c.mode === 'flat') return c.flatFee;
  let fee = Math.ceil(miles / 5) * c.per5;
  if (c.feeCap) fee = Math.min(fee, c.feeCap);
  return fee;
}

function resolve(zip, data, c) {
  const region = regionFor(zip, data);
  const cadence = region ? { label: region.label, weeks: region.weeks } : null;
  const note = noteFor(zip, data);
  if (data.served.has(zip)) return { state: 'served', zip, fee: 0, miles: 0, cadence, note };
  const miles = data.surcharge[zip];
  const fee = feeForMiles(miles, c);
  // A ZIP that resolves to a $0 fee (shares a centroid with a served ZIP) is served, not "surcharge $0".
  if (fee === 0) return { state: 'served', zip, fee: 0, miles, cadence, note };
  if (fee != null) return { state: 'surcharge', zip, fee, miles, cadence, note };
  return { state: 'waitlist', zip, fee: null, miles: miles == null ? null : miles, cadence: null };
}

function cadenceSentence(cadence) {
  if (!cadence) return '';
  return ' We deliver to ' + cadence.label + ' once every ' + cadence.weeks + ' weeks.';
}

/* Reads the stored verdict, dropping anything stamped for another schema or
 * past its 30-day life. Entries written before the stamp existed carry neither
 * field; those are migrated rather than dropped, since the ZIP is all the
 * re-resolve needs and dropping them would re-ask every returning visitor for a
 * ZIP they already gave us. */
function readCache() {
  let cached = null;
  try { cached = JSON.parse(localStorage.getItem('aw_delivery')); } catch (_) {}
  if (!cached || !cached.zip) return null;
  if (cached.v == null && cached.exp == null) return cached;
  if (cached.v !== CACHE_VERSION || !(Number(cached.exp) > Date.now())) {
    clearEligibility();
    return null;
  }
  return cached;
}

/* The stored ZIP on its own, with none of readCache()'s version or expiry side
 * effects: the load-failure path only needs to know which ZIP the customer is
 * walking away from, and must not clear anything just by asking. */
function storedZip() {
  try {
    const raw = JSON.parse(localStorage.getItem('aw_delivery'));
    return raw && raw.zip ? String(raw.zip) : null;
  } catch (_) { return null; }
}

/* Drops the verdict from both places it lives. A waitlist ZIP has to clear the
 * cart attributes too (Brian, 2026-07-21): persist() was the only writer, so an
 * earlier served ZIP stayed cached and stayed on the cart, and the customer
 * could subscribe to an address we do not deliver to. Names only its own keys.
 * By default it also fires a null 'aw:eligibility' (Brian, 2026-07-21), for the
 * callers that have no verdict left to announce: the change-ZIP button, an
 * expired or re-stamped cache, and the fail-closed submit path. An arm that read
 * the verdict at boot has no other way to learn it is gone, so its CTA stayed
 * live on a verdict that no longer existed.
 * The waitlist callers pass { silent: true }: render() has just dispatched the
 * waitlist verdict, and a null one tick later would wipe the very verdict both
 * arms need to draw their waitlist state. They still get the cache drop and the
 * cart-attribute clear, which is the part they actually need. */
async function clearEligibility(opts) {
  try { localStorage.removeItem('aw_delivery'); } catch (_) {}
  if (!(opts && opts.silent)) document.dispatchEvent(new CustomEvent('aw:eligibility', { detail: null }));
  const write = lastWrite.then(() => fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: {
        'Delivery ZIP': '',
        'Delivery Eligibility': '',
        'Delivery Fee': '',
        'Delivery Cadence': '',
        'Delivery Note': '',
      },
    }),
  })).catch(() => { /* non-blocking, and the queue must survive it */ });
  lastWrite = write;
  await write;
}

async function persist(res) {
  const stored = Object.assign({}, res, { v: CACHE_VERSION, exp: Date.now() + CACHE_MAX_AGE_MS });
  try { localStorage.setItem('aw_delivery', JSON.stringify(stored)); } catch (_) {}
  const write = lastWrite.then(() => fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: {
        'Delivery ZIP': res.zip,
        'Delivery Eligibility': res.state,
        'Delivery Fee': res.fee == null ? '' : String(res.fee),
        'Delivery Cadence': res.cadence ? 'once every ' + res.cadence.weeks + ' weeks (' + res.cadence.label + ')' : '',
        // Flags Oregon pre-orders so ops can hold them for the Q4 2026 launch.
        'Delivery Note': res.note && res.note.note ? res.note.note : '',
      },
    }),
  })).catch(() => { /* non-blocking, and the queue must survive it */ });
  lastWrite = write;
  await write;
}

/* Same customer-facing money format as aw-money.liquid / usd() in the other
 * aw-* modules (fees are whole dollars, so decimals only ever appear if the
 * fee config changes to a fractional value). */
function money(n) {
  const v = Number(n);
  const digits = v % 1 === 0 ? 0 : 2;
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: 2 });
}

function render(res, els) {
  const { result, waitlist, changeBtn } = els;
  waitlist.hidden = true;
  result.hidden = false;
  changeBtn.hidden = false;
  if (res.state === 'served') {
    result.className = 'aw-gate__result is-yes';
    if (res.note && res.note.note) {
      // Orderable now, but the note sets delivery expectations (Oregon Q4 2026).
      result.textContent = res.note.note + (res.note.cta ? ' ' + res.note.cta : '');
    } else {
      result.textContent = res.cadence
        ? 'Free delivery to ' + res.cadence.label + ', once every ' + res.cadence.weeks + ' weeks.'
        : 'We deliver to you, and delivery in your area is free.';
    }
  } else if (res.state === 'surcharge') {
    result.className = 'aw-gate__result is-fee';
    result.textContent = 'Good news, we deliver to you. You are just outside our core area, so a ' + money(res.fee) + ' delivery fee applies to each delivery.' + cadenceSentence(res.cadence);
  } else {
    result.className = 'aw-gate__result is-no';
    result.textContent = 'We are not in ' + res.zip + ' yet. Add your email and we will tell you the moment we are.';
    waitlist.hidden = false;
  }
  document.dispatchEvent(new CustomEvent('aw:eligibility', { detail: res }));
}

/* Lookup data would not load. Same affordance as the 5-digit error above, and
 * Check stays live so the customer can retry. */
function renderLoadError(result) {
  result.hidden = false;
  result.className = 'aw-gate__result is-no';
  result.textContent = 'Something went wrong on our end, please tap Check to try again.';
}

async function joinWaitlist(res, c, emailInput) {
  const email = (emailInput.value || '').trim();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { emailInput.focus(); return false; }
  if (!c.klaviyoKey || !c.klaviyoList) return false;
  const body = {
    data: {
      type: 'subscription',
      attributes: {
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email,
              properties: { waitlist_zip: res.zip, source: 'zip-gate waitlist' },
            },
          },
        },
      },
      relationships: { list: { data: { type: 'list', id: c.klaviyoList } } },
    },
  };
  try {
    const r = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + c.klaviyoKey, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify(body),
    });
    return r.ok || r.status === 202;
  } catch (_) { return false; }
}

function init(el) {
  const c = cfg(el);
  const form = document.getElementById('aw-zip-form');
  const input = document.getElementById('aw-zip-input');
  const result = document.getElementById('aw-zip-result');
  const waitlist = document.getElementById('aw-zip-waitlist');
  const emailInput = document.getElementById('aw-zip-email');
  const joinBtn = document.getElementById('aw-zip-join');
  const changeBtn = document.getElementById('aw-zip-change');
  const els = { result, waitlist, changeBtn };

  input.addEventListener('focus', () => loadData(c), { once: true });

  // Returning visitor: show their saved state, offer a change link, never re-ask.
  // Brian, 2026-07-21: the saved ZIP is re-resolved against current data instead
  // of replayed, so a ZIP that moved onto a new route (33149 to the 2-week
  // key-biscayne run, 90042 to weekly) self-heals on the next visit. Nothing
  // renders until the verdict is current, so no wrong cadence flashes first.
  // The re-resolved result is then re-asserted onto this visit's cart, which is
  // how the Delivery Note (the Oregon pre-order hold flag) reaches a cart the
  // visitor did not check their ZIP on.
  // If the visitor checks a ZIP or clears theirs while the restore is still in
  // flight, the restore must not land on top of that newer verdict.
  let restoreDropped = false;

  async function restore(cached) {
    const data = await loadData(c);
    if (restoreDropped) return;
    if (!data) {
      // Data unreachable: fall back to the stored verdict rather than blanking a
      // returning visitor. Not persisted, since it could not be re-checked.
      render(cached, els);
      return;
    }
    const res = resolve(cached.zip, data, c);
    render(res, els);
    // Silent: render() just dispatched this waitlist verdict, and the arms have
    // to keep it to draw their waitlist state.
    if (res.state === 'waitlist') clearEligibility({ silent: true });
    else persist(res);
  }

  const cachedRes = readCache();
  if (cachedRes) {
    // Brian, 2026-07-21: restore() is fire-and-forget, so its promise is caught
    // here. A malformed cached entry must not take the rest of init() (the
    // change button and the submit handler) down with it, and must not surface
    // as an unhandled rejection.
    try {
      input.value = cachedRes.zip;
      restore(cachedRes).catch(() => {});
    } catch (_) { /* boot continues without the restore */ }
  }

  changeBtn.addEventListener('click', () => {
    restoreDropped = true;
    input.value = '';
    result.hidden = true;
    waitlist.hidden = true;
    changeBtn.hidden = true;
    // Brian, 2026-07-21: this used to hand-roll half a clear, dropping the cache
    // but leaving the Delivery* attributes on the cart, so a customer who
    // changed their ZIP still checked out against the old verdict. One
    // implementation now. Not silent: there is a real removal to announce.
    clearEligibility();
    input.focus();
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    restoreDropped = true;
    const zip = (input.value || '').replace(/\D/g, '').slice(0, 5);
    if (zip.length !== 5) {
      result.hidden = false;
      result.className = 'aw-gate__result is-no';
      result.textContent = 'Please double-check your ZIP, it should be 5 digits.';
      return;
    }
    const data = await loadData(c);
    if (!data) {
      /* Brian, 2026-07-21: fail closed, but only for a ZIP the customer has
       * actually changed. Typing a different ZIP is them telling us the saved
       * verdict belongs to an address they have left, so it goes and both arms
       * re-block until a real answer lands. A blip while re-checking the SAME
       * ZIP keeps the saved verdict: it is still the right answer for that ZIP,
       * and blocking every session on a CDN hiccup costs more than it protects.
       * The trade-off left standing: a first-ever visitor whose lookup never
       * loads has no verdict to clear, and the arms already block on that. */
      const prev = storedZip();
      if (prev && prev !== zip) clearEligibility();
      renderLoadError(result);
      return;
    }
    const res = resolve(zip, data, c);
    render(res, els);
    // A waitlist verdict clears the saved one instead of writing nothing, or the
    // previously checked served ZIP survives into the next visit. Silent, since
    // render() has already dispatched the waitlist verdict the arms render from.
    if (res.state === 'waitlist') clearEligibility({ silent: true });
    else persist(res);
  });

  joinBtn.addEventListener('click', async () => {
    const zip = (input.value || '').replace(/\D/g, '').slice(0, 5);
    const data = await loadData(c);
    if (!data) { renderLoadError(result); return; }
    const res = resolve(zip, data, c);
    joinBtn.disabled = true;
    const ok = await joinWaitlist(res, c, emailInput);
    joinBtn.disabled = false;
    result.hidden = false;
    result.className = 'aw-gate__result ' + (ok ? 'is-yes' : 'is-no');
    result.textContent = ok
      ? 'You are on the list. We will email you the moment we deliver to ' + res.zip + '.'
      : 'Something went wrong, please try again.';
  });
}
